import React, { useEffect, useState } from "react"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import bootstrapPlugin from "@fullcalendar/bootstrap"
import { Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Badge } from "reactstrap"
import { get } from "helpers/api_helper"
import moment from "moment"

// Import FullCalendar CSS
import "@fullcalendar/common/main.css"
import "@fullcalendar/daygrid/main.css"
import "@fullcalendar/bootstrap/main.css"

const OrdersCalendar = () => {
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [allOrders, setAllOrders] = useState([])
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedDateOrders, setSelectedDateOrders] = useState([])
  const [hoveredIdx, setHoveredIdx] = useState(null)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)

  const loadDueOrders = async (start, end) => {
    setLoading(true)
    try {
      const res = await get(`api/orders/due_orders?start=${start}&end=${end}`)
      if (res.status === 200 && Array.isArray(res.response)) {
        setAllOrders(res.response)

        // Group by start date to count due orders
        const countsByDate = {}
        res.response.forEach(order => {
          const dateStr = moment(order.due_date).format("YYYY-MM-DD")
          if (!countsByDate[dateStr]) {
            countsByDate[dateStr] = []
          }
          countsByDate[dateStr].push(order)
        })

        // Format as count events
        const formattedEvents = Object.keys(countsByDate).map(dateStr => {
          const orders = countsByDate[dateStr]
          const count = orders.length
          return {
            id: dateStr,
            title: `${count} Due`,
            start: dateStr,
            allDay: true,
            color: "#556ee6", // Theme primary color
            extendedProps: {
              date: dateStr,
              orders: orders
            }
          }
        })
        setEvents(formattedEvents)

        // Auto-select a date if none is currently selected
        let dateToSelect = selectedDate
        if (!dateToSelect) {
          const todayStr = moment().format("YYYY-MM-DD")
          const todayHasOrders = res.response.some(order => 
            moment(order.due_date).format("YYYY-MM-DD") === todayStr
          )
          if (todayHasOrders) {
            dateToSelect = todayStr
          } else if (res.response.length > 0) {
            dateToSelect = moment(res.response[0].due_date).format("YYYY-MM-DD")
          }
        }

        if (dateToSelect) {
          setSelectedDate(dateToSelect)
          const ordersForDate = res.response.filter(order => 
            moment(order.due_date).format("YYYY-MM-DD") === dateToSelect
          )
          setSelectedDateOrders(ordersForDate)
        } else {
          setSelectedDateOrders([])
        }
      } else {
        setAllOrders([])
        setEvents([])
        setSelectedDateOrders([])
      }
    } catch (err) {
      console.error("Error loading due orders:", err)
    } finally {
      setLoading(false)
    }
  }

  // Update selectedDateOrders if allOrders changes
  useEffect(() => {
    if (selectedDate && allOrders.length > 0) {
      const ordersForDate = allOrders.filter(order => 
        moment(order.due_date).format("YYYY-MM-DD") === selectedDate
      )
      setSelectedDateOrders(ordersForDate)
    }
  }, [selectedDate, allOrders])

  const handleEventClick = (info) => {
    const clickedDateStr = info.event.extendedProps.date
    setSelectedDate(clickedDateStr)
    setSelectedDateOrders(info.event.extendedProps.orders || [])
  }

  const handleDateClick = (info) => {
    const clickedDateStr = info.dateStr
    setSelectedDate(clickedDateStr)
    const ordersForDate = allOrders.filter(order => 
      moment(order.due_date).format("YYYY-MM-DD") === clickedDateStr
    )
    setSelectedDateOrders(ordersForDate)
  }

  const handleDatesSet = (dateInfo) => {
    loadDueOrders(dateInfo.startStr, dateInfo.endStr)
  }

  const handleOrderListClick = (order) => {
    setSelectedOrder(order)
    setModalOpen(true)
  }

  const downloadCSV = () => {
    if (!selectedDateOrders || selectedDateOrders.length === 0) return

    const headers = ["Order Number", "Order Type", "Client Name", "Patient Name", "Case No", "Priority", "Order Date", "Due Date", "Products"]
    
    const getPriorityText = (priority) => {
      const p = parseInt(priority)
      if (p === 1) return "Low"
      if (p === 2) return "Medium"
      if (p === 3) return "High"
      if (p === 4) return "Urgent"
      return "Standard"
    }

    const getWorkTypeText = (worktype) => {
      if (worktype === "new") return "New"
      if (worktype === "redo") return "Redo"
      if (worktype === "corr" || worktype === "curr") return "Correction"
      return worktype
    }

    const csvRows = []
    csvRows.push(headers.join(","))

    selectedDateOrders.forEach(order => {
      const row = [
        order.order_number ? order.order_number.toString() : "",
        getWorkTypeText(order.worktype) || "",
        order.client_name ? order.client_name.toString() : "",
        order.patient_name ? order.patient_name.toString() : "",
        order.case_no ? order.case_no.toString() : "",
        getPriorityText(order.priority) || "",
        order.order_date ? moment(order.order_date).format("YYYY-MM-DD") : "",
        order.due_date ? moment(order.due_date).format("YYYY-MM-DD HH:mm") : "",
        order.products_list ? order.products_list.toString() : ""
      ]
      csvRows.push(row.map(val => `"${val.replace(/"/g, '""')}"`).join(","))
    })

    const csvContent = csvRows.join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    const dateFilename = moment(selectedDate).format("YYYY-MM-DD")
    link.setAttribute("download", `Due_Orders_${dateFilename}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const toggleModal = () => setModalOpen(!modalOpen)

  const getPriorityBadge = (priority) => {
    const p = parseInt(priority)
    if (p === 1) return <Badge color="info">Low</Badge>
    if (p === 2) return <Badge color="primary">Medium</Badge>
    if (p === 3) return <Badge color="warning">High</Badge>
    if (p === 4) return <Badge color="danger">Urgent</Badge>
    return <Badge color="secondary">Standard</Badge>
  }

  return (
    <React.Fragment>
      <Card className="mt-4 shadow-sm border-0">
        <CardBody style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}
          <Row>
            {/* Calendar Column */}
            <Col lg="7" md="12" className="border-end">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h4 className="card-title mb-0 font-size-16">
                  <i className="mdi mdi-calendar-clock text-primary me-2 font-size-20"></i>
                  Due Orders Calendar
                </h4>
                <div className="d-flex gap-3 font-size-12">
                  <span className="d-flex align-items-center">
                    <span className="badge p-1 bg-primary me-1"> </span> Due Count
                  </span>
                </div>
              </div>

              <div className="orders-calendar-container">
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin, bootstrapPlugin]}
                  initialView="dayGridMonth"
                  themeSystem="bootstrap"
                  events={events}
                  eventClick={handleEventClick}
                  dateClick={handleDateClick}
                  datesSet={handleDatesSet}
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,dayGridWeek"
                  }}
                  height="auto"
                />
              </div>
            </Col>

            {/* List Column */}
            <Col lg="5" md="12" className="ps-lg-4 mt-4 mt-lg-0">
              <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                <h4 className="card-title mb-0 font-size-15">
                  <i className="mdi mdi-clipboard-list-outline text-muted me-1 font-size-18"></i>
                  Due: {selectedDate ? moment(selectedDate).format("DD MMM YYYY") : "Select Date"}
                </h4>
                {selectedDateOrders.length > 0 && (
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    className="btn-rounded"
                    onClick={downloadCSV}
                  >
                    <i className="mdi mdi-download me-1"></i> Download CSV
                  </Button>
                )}
              </div>

              <div 
                className="due-orders-list-scroll" 
                style={{ 
                  maxHeight: "460px", 
                  overflowY: "auto", 
                  paddingRight: "5px",
                  scrollBehavior: 'smooth'
                }}
              >
                {selectedDateOrders.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="mdi mdi-calendar-blank display-4 text-light"></i>
                    <p className="mt-2 font-size-14">No orders due on this date</p>
                  </div>
                ) : (
                  selectedDateOrders.map((order, idx) => (
                    <Card 
                      key={idx} 
                      className="mb-2 shadow-none border" 
                      style={{ 
                        transition: 'all 0.25s ease-in-out', 
                        cursor: 'pointer',
                        borderColor: hoveredIdx === idx ? '#556ee6' : '#eff2f7',
                        boxShadow: hoveredIdx === idx ? '0 0.5rem 1rem rgba(0, 0, 0, 0.08)' : 'none',
                        transform: hoveredIdx === idx ? 'translateY(-2px)' : 'none',
                        background: hoveredIdx === idx ? 'rgba(85, 110, 230, 0.02)' : '#fff'
                      }} 
                      onMouseEnter={() => setHoveredIdx(idx)}
                      onMouseLeave={() => setHoveredIdx(null)}
                      onClick={() => handleOrderListClick(order)}
                    >
                      <CardBody className="p-3">
                        <Row className="align-items-center">
                          {/* Order Number & Badges */}
                          <Col sm="4" xs="12" className="mb-2 mb-sm-0">
                            <div className="fw-bold font-size-13 text-primary mb-1">
                              {order.order_number}
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {order.worktype === "redo" && <Badge color="danger" style={{ fontSize: "10px", padding: "3px 6px" }}>Redo</Badge>}
                              {order.worktype === "new" && <Badge color="success" style={{ fontSize: "10px", padding: "3px 6px" }}>New</Badge>}
                              {(order.worktype === "corr" || order.worktype === "curr") && <Badge color="warning" style={{ fontSize: "10px", padding: "3px 6px" }}>Correction</Badge>}
                              {getPriorityBadge(order.priority)}
                            </div>
                          </Col>

                          {/* Client & Patient Info */}
                          <Col sm="5" xs="12" className="mb-2 mb-sm-0">
                            <div className="font-size-12 text-dark text-truncate mb-1" title={order.client_name}>
                              <i className="mdi mdi-bank-outline me-1 text-muted"></i>
                              <strong>Client:</strong> {order.client_name || "N/A"}
                            </div>
                            <div className="font-size-12 text-muted text-truncate" title={order.patient_name}>
                              <i className="mdi mdi-account-outline me-1"></i>
                              <strong>Patient:</strong> {order.patient_name || "N/A"}
                            </div>
                          </Col>

                          {/* Time & View Details Link */}
                          <Col sm="3" xs="12" className="text-sm-end">
                            <div className="font-size-11 text-muted mb-2">
                              <i className="mdi mdi-clock-outline me-1"></i>
                              {order.due_date ? moment(order.due_date).format("hh:mm A") : "N/A"}
                            </div>
                            <span className="text-primary font-size-11 fw-semibold">
                              Details <i className="mdi mdi-arrow-right"></i>
                            </span>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  ))
                )}
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Order Details Modal */}
      {selectedOrder && (
        <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
          <ModalHeader toggle={toggleModal} className="bg-primary text-white">
            Order Details: {selectedOrder.order_number}
          </ModalHeader>
          <ModalBody className="p-4">
            <Row className="mb-3">
              <Col xs="6">
                <p className="text-muted mb-1 font-size-12">Client Name</p>
                <h5 className="font-size-14 text-truncate">{selectedOrder.client_name || "N/A"}</h5>
              </Col>
              <Col xs="6">
                <p className="text-muted mb-1 font-size-12">Patient Name</p>
                <h5 className="font-size-14">{selectedOrder.patient_name || "N/A"}</h5>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="6">
                <p className="text-muted mb-1 font-size-12">Case / Model No</p>
                <h5 className="font-size-14">{selectedOrder.case_no || "N/A"}</h5>
              </Col>
              <Col xs="6">
                <p className="text-muted mb-1 font-size-12">Priority</p>
                <div>{getPriorityBadge(selectedOrder.priority)}</div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="6">
                <p className="text-muted mb-1 font-size-12">Order Date</p>
                <h5 className="font-size-14">
                  {selectedOrder.order_date ? moment(selectedOrder.order_date).format("DD MMM YYYY") : "N/A"}
                </h5>
              </Col>
              <Col xs="6">
                <p className="text-muted mb-1 font-size-12">Due Date</p>
                <h5 className="font-size-14 text-danger font-weight-bold">
                  {selectedOrder.due_date ? moment(selectedOrder.due_date).format("DD MMM YYYY HH:mm") : "N/A"}
                </h5>
              </Col>
            </Row>

            <div className="mb-0">
              <p className="text-muted mb-2 font-size-12">Products</p>
              {selectedOrder.products_list ? (
                <div className="d-flex flex-wrap gap-2">
                  {selectedOrder.products_list.split(", ").map((product, idx) => (
                    <Badge key={idx} color="light" className="border text-dark p-2 font-size-12">
                      {product}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted italic font-size-13">No products added</p>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>Close</Button>
            <a href={`/orders`} className="btn btn-primary">
              Go to Orders
            </a>
          </ModalFooter>
        </Modal>
      )}
    </React.Fragment>
  )
}

export default OrdersCalendar
